import { useState, useMemo } from 'react'
import {Navigate, useParams} from 'react-router-dom'
import css from './WorkspaceProjectsPage.module.css'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectAllProjects, selectProjectsLoading } from '../../../store/projects/projectsSelectors'
import { selectIsWorkspacePro } from '@/store/workspace/workspaceSelectors'
import { FREE_PLAN_PROJECTS_LIMIT } from '@/const/constants'
import type { RootState } from '../../../store'
import { createProject as createProjectAction } from '@/store/projects/projectsSlice'
import { selectOpenDropdownId, selectIsLoading } from '../../../store/ui/uiSelectors'
import { setOpenDropdown } from '../../../store/ui/uiSlice'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import IconButton from '@/components/ui/IconButton/IconButton'
import OptionsIcon from '@/assets/icons/icon-options.svg?react'
import CheckIcon from '../../../assets/icons/icon-check.svg?react'
import WorkspaceProjectsItem from "./components/WorkspaceProjectsItem"
import WorkspaceProjectsNew from "./components/WorkspaceProjectsNew"
import Empty from '@/components/ui/Empty/Empty'
import Button from '@/components/ui/Button/Button'
import PlateLoader from '@/components/ui/Loader/PlateLoader'
import {useWorkspace} from "@/contexts/WorkspaceContext.tsx";

type SortOrder = 'alphabetical' | 'updated'

const STORAGE_KEY = 'projects_sort_order'
const DROPDOWN_ID = 'projects-sort-dropdown'

const getSavedSort = (): SortOrder => {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved === 'updated' ? 'updated' : 'alphabetical'
}

const WorkspaceProjectsPage = () => {
    const dispatch = useAppDispatch()
    const { workspace: workspaceUrlId } = useParams<{ workspace: string }>()
    const projects = useAppSelector(selectAllProjects)
    const loading = useAppSelector(selectProjectsLoading)
    const creatingProjectId = useAppSelector((state: RootState) => state.projects.creatingProjectId)
    const userId = useAppSelector(state => state.auth?.id ?? '')
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const [sortOrder, setSortOrder] = useState<SortOrder>(getSavedSort)
    const isCreating = useAppSelector(selectIsLoading('create-project'))
    const isPro = useAppSelector(selectIsWorkspacePro)

    const { workspace, loading: workspaceLoading } = useWorkspace()

    const isDropdownOpen = openDropdownId === DROPDOWN_ID
    const atProjectLimit = !isPro && projects.length >= FREE_PLAN_PROJECTS_LIMIT

    const handleNewProject = () => {
        dispatch(createProjectAction({ name: '', workspaceId: workspace!.id, userId }))
    }

    const handleSortChange = (order: SortOrder) => {
        setSortOrder(order)
        localStorage.setItem(STORAGE_KEY, order)
        dispatch(setOpenDropdown(null))
    }

    const sortedProjects = useMemo(() => {
        return [...projects]
            .filter(p => p.id !== creatingProjectId)
            .sort((a, b) => {
                if (sortOrder === 'updated') {
                    const aTime = a.updatedAt?.seconds ?? 0
                    const bTime = b.updatedAt?.seconds ?? 0
                    return bTime - aTime
                }
                return (a.name || '').localeCompare(b.name || '')
            })
    }, [projects, sortOrder, creatingProjectId])

    // Gate on the workspace doc too, not just projects: the splash now drops on shell-ready
    // (auth + workspace list), so this page can mount before listenWorkspaceDoc's first
    // snapshot. If the projects snapshot wins that race, `workspace` is still null and the
    // guard below would wrongly bounce to "/" (SCD-890 follow-up).
    if (loading || workspaceLoading) {
        return <PlateLoader fullscreen />
    }

    if (!workspace || workspace.workspaceUrlId !== workspaceUrlId) {
        return <Navigate to={"/"} />
    }

    if (projects.length === 0) {
        return (
            <div className={css.container}>
                <Empty
                    title="Projects"
                    text="Start by creating a new project."
                    button={<Button title="Create project" type="light" size="medium" loading={isCreating} onClick={handleNewProject} testId="new-project-btn" />}
                />
            </div>
        )
    }

    return (
        <div className={css.container}>
            <div className={css.header_top_right}>
                <DropdownWrapper>
                    <IconButton grey={true} icon={<OptionsIcon />} onClick={() => dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))} />
                    <Dropdown isOpen={isDropdownOpen} onClose={() => dispatch(setOpenDropdown(null))}>
                        <DropdownItem
                            title="Alphabetical"
                            rightIcon={sortOrder === 'alphabetical' ? <CheckIcon /> : undefined}
                            onClick={() => handleSortChange('alphabetical')}
                        />
                        <DropdownItem
                            title="Last updated"
                            rightIcon={sortOrder === 'updated' ? <CheckIcon /> : undefined}
                            onClick={() => handleSortChange('updated')}
                        />
                    </Dropdown>
                </DropdownWrapper>
            </div>
            <div className={css.list}>
                <WorkspaceProjectsNew onClick={handleNewProject} loading={isCreating} atLimit={atProjectLimit} />
                {sortedProjects.map(project => (
                    <WorkspaceProjectsItem key={project.id} project={project} />
                ))}
            </div>
        </div>
    )
}

export default WorkspaceProjectsPage
